import { Entry, fetchWithTenant, sanitiseUrlForEnvironment, useTenant } from '@parcel2go/npm-p2g-app';
import useSWR from 'swr';
import { CONTENT_API } from '~/lib/constants';
import { captureWarning } from '~/lib/metrics';

interface ContentResponse {
  data: Entry<ContentEntryFields>[];
}

export interface ContentEntryFields {
  id?: string;
  name?: string;
}

/**
 * Fetches a CMS content group on its own, outside the route-scoped `ContentContext`.
 *
 * `ContentProvider` holds exactly one content group at a time - whichever the current route set via
 * `SET_CONTENT_GROUP` - so `CmsContent` can only see entries belonging to that group. Side panels
 * that open over any route (PrePay top-up, the booking summary) therefore can't rely on it. This
 * hook lets those surfaces read a fixed group regardless of where the customer happens to be, so
 * marketing maintains a single entry per slot.
 *
 * Prefer `CmsContent` / `LazyCmsContent` for content that belongs to a single route.
 */
export function useCmsContentGroup(group: string) {
  const { tenant, settings } = useTenant();

  const { data, isLoading } = useSWR<Entry<ContentEntryFields>[]>(
    group ? `cms-content-group-${group}-${tenant}` : null,
    async () => {
      const url = sanitiseUrlForEnvironment(`${CONTENT_API}/api/uis/v5/content/${group}`);
      const response = await fetchWithTenant(tenant, settings, url);
      if (!response.ok) {
        captureWarning('Failed to load standalone CMS content group', { section: 'cms', group });
        return [];
      }
      const result: ContentResponse = await response.json();
      return result?.data ?? [];
    },
    { revalidateOnFocus: false, revalidateOnReconnect: false }
  );

  return { entries: data ?? [], isLoading } as const;
}

/** Finds an entry by its `id` or `name` field, matching how `CmsContent` resolves a content key. */
export function findContentEntry(entries: Entry<ContentEntryFields>[], contentKey: string) {
  return entries.find((entry) => entry.fields.id === contentKey || entry.fields.name === contentKey);
}
